import htmlParser from 'html-react-parser';
import type { PageEntry, Entry, Node } from 'contensis-delivery-api';
import { useTheme } from 'styled-components';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import cmsParser from 'Common/util/cmsParser';
import type { Props } from './Subject';

import { H3 } from 'Elements/Typography';

import ProfileCard from 'Components/Card/SolentStory/Profile/Profile';

import { AccreditationsWrapper, AccreditationsListItem } from './Subject.styled';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
    courses: Entry[];
    courseListingFilters: Entry;
    facilities: Entry[];
    solentStories: Entry[];
    news: Entry[];
};

const datamap = (data: PageData): Props => {
    const { entry, ancestors, courses, courseListingFilters, facilities, solentStories, news } =
        data;
    const { title, sys, heroBanner } = entry;

    const subjectAreaParam = encodeURIComponent(entry.subjectArea.key);

    const { colors } = useTheme();

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        heroBanner: {
            ...cmsParser.heroBannerWave(heroBanner.heroBannerWaveDefault),
            image: heroBanner.image ? cmsParser.image(heroBanner.image) : undefined
        },
        introduction: {
            text: htmlParser(entry.subjectOverview),
            video: entry.video ? cmsParser.transcriptEmbed(entry.video) : undefined,
            carousel:
                entry.carouselImages.length > 0
                    ? {
                          id: 'subject_introduction_carousel',
                          slides: entry.carouselImages.map((item, index) => ({
                              id: index,
                              element: (
                                  <Image
                                      height={742.5}
                                      width={1320}
                                      style={{
                                          maxWidth: '100%',
                                          height: 'auto',
                                          objectFit: 'cover'
                                      }}
                                      {...cmsParser.image(item)}
                                  />
                              )
                          })),
                          slidesConfig: {
                              large: {
                                  slidesToShow: 1,
                                  gap: 1
                              },
                              medium: {
                                  slidesToShow: 1,
                                  gap: 1
                              },
                              small: {
                                  slidesToShow: 1,
                                  gap: 1
                              }
                          }
                      }
                    : undefined,
            listHeading: { heading: entry.ksPsHeading, colour: colors.accents[5].hex },
            list:
                entry.ksPsList.length > 0
                    ? {
                          items: entry.ksPsList.map((item, index) => ({
                              id: index,
                              icon: dynamic(() =>
                                  import('@solent-university/solent-icons').then(
                                      icons => icons[item.icon.name]
                                  )
                              ),
                              iconColour: colors.accents[5].hex,
                              text: item.text
                          }))
                      }
                    : undefined,
            accreditations:
                entry.accreditations.length > 0
                    ? {
                          heading: entry.accreditationsHeading
                              ? entry.accreditationsHeading
                              : undefined,
                          backgroundColour: colors.accents[5].hex,
                          colour: colors.secondary.hex,
                          children: (
                              <AccreditationsWrapper>
                                  {entry.accreditations.map((item, index) => (
                                      <AccreditationsListItem key={index}>
                                          <Image
                                              fill
                                              style={{ objectFit: 'contain' }}
                                              {...cmsParser.image(item.image)}
                                          />
                                      </AccreditationsListItem>
                                  ))}
                              </AccreditationsWrapper>
                          )
                      }
                    : undefined
        },
        courseMiniListing: {
            id: 'subject_courses',
            heading: entry.courseListingHeading,
            courses: courses.map(course => {
                const studyLevel: string | undefined =
                    course.courseListingFilterOptions?.studyLevel || undefined;
                const courseOptions: string[] =
                    course.courseListingFilterOptions?.courseOptions || [];

                return {
                    id: course.sys.id,
                    degreeType:
                        studyLevel && course.degreeType && course.degreeType !== 'none'
                            ? course.degreeType
                            : course.sys.contentTypeId === 'courseFoundationYears'
                            ? 'Foundation year'
                            : courseOptions.includes('Apprenticeship')
                            ? 'Apprenticeship'
                            : courseOptions.includes('Professional')
                            ? 'Professional'
                            : '',
                    courseName: course.name,
                    link: course.sys.uri,
                    studyLevel: studyLevel,
                    categories: course.courseCategory.map(category => category.name),
                    courseOptions:
                        course.courseListingFilterOptions.courseOptions.length > 0
                            ? course.courseListingFilterOptions.courseOptions
                            : undefined,
                    image: {
                        src: cmsParser.image(course.courseCardImage).src
                    },
                    status: course.state ? course.state.toLowerCase() : 'open'
                };
            }),
            paddingTop: true,
            promo: {
                promoText: 'Explore more of our subjects and courses',
                arrowLink: {
                    href: 'https://www.solent.ac.uk/subjects',
                    label: 'View all subjects',
                    variant: 'fiveGrey'
                },
                arrowButton: {
                    href: 'https://www.solent.ac.uk/courses',
                    label: 'View all courses',
                    variant: 'accentFiveGrey',
                    type: 'button'
                }
            },
            filterProps: {
                id: 'subject_courses_filters',
                filters: [
                    {
                        type: 'studyLevel',
                        label: 'Study level',
                        options: courseListingFilters.studyLevelFilter.map(level => ({
                            id: level.value,
                            name: level.label,
                            label: level.label,
                            value: level.value
                        }))
                    },
                    {
                        type: 'courseOptions',
                        label: 'Course options',
                        options: courseListingFilters.courseOptionsFilter.map(option => ({
                            id: option.value,
                            name: option.label,
                            label: option.label,
                            value: option.value
                        }))
                    }
                ],
                filterInfoModal: entry.modalText
                    ? {
                          linkText: entry.modalLinkText
                              ? entry.modalLinkText
                              : "What's a study level?",
                          modalChildren: <>{htmlParser(entry.modalText)}</>
                      }
                    : undefined
            }
        },
        industryLinks:
            entry.industryLinks.length > 0
                ? {
                      heading: entry.industryLinksHeading
                          ? entry.industryLinksHeading
                          : 'Community and industry links',
                      description: entry.industryIntroText ? entry.industryIntroText : undefined,
                      items: entry.industryLinks.map((item, index) => ({
                          id: index,
                          image: cmsParser.image(item.image)
                      })),
                      link: entry.link
                          ? { ...cmsParser.arrowLink(entry.link), variant: 'five' }
                          : undefined,
                      disclaimer: entry.disclaimer ? entry.disclaimer : undefined
                  }
                : undefined,
        solentStories:
            solentStories.length > 0
                ? {
                      heading: 'Graduate stories',
                      text: entry.graduateStoriesIntroText
                          ? entry.graduateStoriesIntroText
                          : 'Hear from Solent alumni about where their careers have taken them and how studying at Solent prepared them for their future.',
                      cta: {
                          label: 'Find out more',
                          ariaLabel: 'Find out more about out Graduate stories',
                          href: 'https://www.solent.ac.uk/graduate-stories',
                          type: 'button'
                      },
                      tabNavigation: {
                          id: 'subject_graduate_stories',
                          tabs: solentStories.map(item => ({
                              name: item.title,
                              avatar: cmsParser.image(item.listingCardImage),
                              id: item.sys.id,
                              position: item.position,
                              organisation: item.student.company
                                  ? item.student.company
                                  : item.graduationYear
                                  ? `Graduated ${item.graduationYear}`
                                  : undefined,
                              content: (
                                  <>
                                      <ProfileCard
                                          featureImage={cmsParser.image(item.cardImage)}
                                          avatar={cmsParser.image(item.listingCardImage)}
                                          quote={item.courseCardQuote}
                                          name={item.title}
                                          position={item.position}
                                          organisation={item.organisation}
                                          link={{
                                              href: item.sys.uri,
                                              label: 'Discover profile'
                                          }}
                                      />
                                  </>
                              )
                          }))
                      }
                  }
                : undefined,
        facilities:
            facilities.length > 0
                ? {
                      id: 'subject_facilities',
                      heading: entry.facilitiesHeading,
                      description: htmlParser(entry.facilitiesIntroText),
                      facilityCards: facilities.map(item => ({
                          heading: item.entryTitle,
                          description: item.intro,
                          link: {
                              href: item.sys.uri,
                              label: 'View facility',
                              ariaLabel: `Click to find out more about the ${item.entryTitle}`,
                              variant: 'five',
                              iconOutline: true,
                              iconBorder: false
                          },
                          image: cmsParser.image(item.image)
                      })),
                      paddingTop: true,
                      showBackground: false
                  }
                : undefined,
        generalPromo: {
            text: entry.openDayPromo.description,
            heading: entry.openDayPromo.heading,
            link: {
                ...cmsParser.linkButton(entry.openDayPromo.buttonCTA),
                variant: 'campaignAccentFive'
            },
            backgroundColor: entry.openDayPromo.backgroundColour
                ? cmsParser.getThemeColour(entry.openDayPromo.backgroundColour)
                : undefined,
            showSectionBackground: true,
            showSectionWave: true
        },
        news:
            news.length > 0
                ? {
                      id: 'subject_news',
                      heading: entry.mediaHubHeading,
                      moreLink: {
                          href: `https://www.solent.ac.uk/media-hub/search?page=1&subjectArea=${subjectAreaParam}&mediaType=newsArticle%2CblogArticle%2Cevent`,
                          label: 'View all news',
                          ariaLabel: 'View all news'
                      },
                      items: news.map(item => cmsParser.benCard(item, false)),
                      showBackground: true
                  }
                : undefined,
        faq: {
            heading: entry.faQsHeading,
            accordions: entry.accordion.map((item, index) => ({
                id: index,
                heading: (
                    <H3
                        size={{ small: '1.25rem', large: '1.5rem' }}
                        spacing={{
                            small: { margin: '0' }
                        }}
                    >
                        {item.title}
                    </H3>
                ),
                headingType: 'h3',
                variant: 'tertiary',
                button: {
                    ariaLabel: 'Read more about our frequently asked questions'
                },
                children: htmlParser(item.content),
                isBackgroundSameColour: true
            }))
        },
        brandPromo: {
            heading: entry.stayUpdatedPromo.heading,
            text: entry.stayUpdatedPromo.description,
            link: {
                ...cmsParser.linkButton(entry.stayUpdatedPromo.buttonCTA),
                variant: entry.stayUpdatedPromo.buttonVariant
                    ? entry.stayUpdatedPromo.buttonVariant
                    : undefined
            },
            backgroundColor: entry.stayUpdatedPromo.backgroundColor
                ? cmsParser.getThemeColour(entry.stayUpdatedPromo.backgroundColor)
                : undefined
        }
    };
};

export default datamap;
